/**
 * User and Organization mapper functions
 * Converts API models to entity models
 */

import type { API } from '../api_clients';
import { User, Organization, OrganizationSettings, Subscription, Address, StateCode } from '../../types';
import { UserStatus, UserRole } from '../../types/enums';

/**
 * Maps API User to Entity User
 *
 * Note: API returns `name` as single string, entity uses `firstName` and `lastName`
 * API uses `unid`, entity uses `id`
 */
export function mapApiUserToEntity(
  apiUser: API.ResponsesUser,
  organizationId?: string
): User {
  return {
    id: apiUser.unid,
    organizationId: organizationId || '', // Must be provided or extracted from context
    email: apiUser.email || '',
    fullName: apiUser.name || (apiUser as any).full_name || (apiUser as any).fullName || '',
    userName: apiUser.username || (apiUser as any).user_name || (apiUser as any).userName || undefined,
    phone: apiUser.phone || undefined,
    avatarUrl: apiUser.profilePictureUrl || undefined,
    status: UserStatus.ACTIVE, // Default status
    isActive: true,
    permissions: [], // Should be populated from API or context
    onboardingComplete: apiUser.onboardingComplete,
    createdAt: apiUser.createdAt,
    updatedAt: apiUser.updatedAt,
  };
}

/**
 * Maps API Organization to Entity Organization
 *
 * Note: API organization has limited fields compared to entity
 * Some fields like dotNumber, mcNumber, settings need to be populated separately
 */
export function mapApiOrganizationToEntity(
  apiOrg: API.ResponsesOrganization
): Organization {
  // Map address
  const address: Address | undefined = apiOrg.address
    ? {
      line1: apiOrg.address.street || '',
      line2: "",
      city: apiOrg.address.city || '',
      state: apiOrg.address.state as StateCode || '',
      zip: apiOrg.address.postal_code || '',
      country: apiOrg.address.country || '',
    }
    : undefined;

  // Map address to settings if available
  const settings: OrganizationSettings | undefined = apiOrg.address
    ? {
      timezone: apiOrg.timeZoneText || 'America/New_York',
      dateFormat: apiOrg.dateFormat || 'MM/DD/YYYY',
      distanceUnit: (apiOrg.distanceUnit as any) || 'miles',
      currency: 'USD',
    }
    : undefined;

  // Create basic subscription info
  const subscription: Subscription | undefined = {
    plan: 'professional',
    status: apiOrg.active ? 'active' : 'canceled',
    currentPeriodEnd: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
  };

  return {
    id: apiOrg.unid || '',
    name: apiOrg.name || '',
    dotNumber: apiOrg.dotNumber,
    mcNumber: apiOrg.mcNumber,
    dba: apiOrg.dba,
    taxId: apiOrg.taxId,
    scac: apiOrg.scac,
    status: apiOrg.active ? 'active' : 'inactive',
    roles: apiOrg.roles,
    settings,
    subscription,
    address,
    businessMode: apiOrg.businessMode,
    timeZoneText: apiOrg.timeZoneText,
    timeZoneOffsetMinutes: apiOrg.timeZoneOffsetMinutes,
    dateFormat: apiOrg.dateFormat,
    distanceUnit: apiOrg.distanceUnit,
    createdAt: apiOrg.createdAt,
    updatedAt: apiOrg.updatedAt,
    email: apiOrg.email,
    phone: apiOrg.phone,
    technicianUnid: apiOrg.technicianUnid,
  };
}


/**
 * Maps login response to both User and Organization entities
 *
 * This is the main mapper used in auth flow
 */
export function mapLoginResponseToEntities(
  response: API.ResponsesLoginResponseData
): {
  user: User;
  organization: Organization | null;
} {
  // Map user (assuming organization info is in response or will be fetched separately)
  const user = mapApiUserToEntity(response.user);

  // Organization might need to be fetched separately or might be embedded
  // For now, return null if not available
  const organization = null;

  return {
    user,
    organization,
  };
}

/**
 * Helper to extract organization ID from user or context
 */
export function extractOrganizationId(
  apiUser: API.ResponsesUser
): string | undefined {
  // Check if organization info is embedded in user response
  // This depends on your API structure
  return undefined;
}

/**
 * Converts entity User back to API format (for updates)
 */
export function mapEntityUserToApi(
  user: User
): Partial<API.ResponsesUser> {
  return {
    unid: user.id,
    name: user.fullName,
    username: user.userName || null,
    email: user.email,
    phone: user.phone || null,
    profilePictureUrl: user.avatarUrl || null,
  };
}

/**
 * Converts entity Organization back to API format (for updates)
 */
export function mapEntityOrganizationToApi(
  org: Organization
): Partial<API.ResponsesOrganization> {
  return {
    unid: org.id,
    name: org.name,
    dba: org.dba,
    dotNumber: org.dotNumber,
    mcNumber: org.mcNumber,
    taxId: org.taxId,
    scac: org.scac,
    dateFormat: org.settings?.dateFormat || org.dateFormat,
    distanceUnit: org.settings?.distanceUnit || org.distanceUnit,
    businessMode: org.settings ? undefined : undefined, // Map from settings if needed
    active: org.status === 'active',
    timeZoneText: org.settings?.timezone || 'America/New_York',
  };
}
